/**
 * <mb-cart-gifts>: brindes do carrinho (portado de offers.js + components/loadCartOffers.js).
 * - Widget flutuante abre o popup; abas alternam as faixas; "SELECIONAR" revela as variantes do brinde.
 * - Ao adicionar, o item vai com a propriedade _brinde (handle da faixa); quem zera o preço é o desconto
 *   automático "Compre X, ganhe Y" da Shopify.
 * - A seção inteira é re-renderizada a cada alteração do carrinho, porque as faixas dependem do total.
 * - Como na Olist: o popup abre sozinho quando um item entra e há faixa liberada sem brinde no carrinho
 *   (offers.js:244-247 do tema anterior); brinde de faixa que deixou de valer, brinde a mais e quantidade
 *   acima de 1 são corrigidos sozinhos (a seção diz quais linhas em data-gifts-remove / data-gifts-fix).
 */
const { emit, storage } = window.MB;

const HIDDEN_KEY = 'mb-gifts-widget-hidden';

class MbCartGifts extends HTMLElement {
  connectedCallback() {
    this.onCartUpdated = this.onCartUpdated.bind(this);
    this.widget = this.querySelector('.popup-offers-widget');
    this.popup = this.querySelector('[data-popup-offers]');

    if (storage.get(HIDDEN_KEY) && this.widget) this.widget.hidden = true;

    // O gatilho também existe fora desta seção: o botão "Brindes disponíveis" fica no rodapé do carrinho.
    this.onDocumentClick = (event) => {
      if (!event.target.closest('[data-open-popup-offers]')) return;
      event.preventDefault();
      this.openPopup();
    };
    document.addEventListener('click', this.onDocumentClick);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-close-popup-offers]')) {
        event.preventDefault();
        this.closePopup();
        return;
      }
      if (event.target.closest('[data-remove-offers-widget]')) {
        event.preventDefault();
        this.hideWidget();
        return;
      }

      const tab = event.target.closest('[data-tab-offer]');
      if (tab) {
        event.preventDefault();
        this.selectTab(tab.dataset.tabOffer);
        return;
      }

      const select = event.target.closest('[data-gift-select]');
      if (select) {
        event.preventDefault();
        this.toggleForm(select.closest('[data-gift-product]'), true);
        return;
      }

      const close = event.target.closest('[data-gift-close]');
      if (close) {
        event.preventDefault();
        this.toggleForm(close.closest('[data-gift-product]'), false);
        return;
      }

      const variant = event.target.closest('[data-gift-variant]');
      if (variant) {
        event.preventDefault();
        this.add(variant);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closePopup();
    });

    document.addEventListener('mb:cart:updated', this.onCartUpdated);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener('mb:cart:updated', this.onCartUpdated);
    document.removeEventListener('click', this.onDocumentClick);
  }

  async onCartUpdated(event) {
    await this.refresh();
    this.sync(event?.detail);
  }

  /**
   * Corrige as linhas de brinde fora da regra; senão, abre o popup quando entrou um item que não é brinde e há
   * faixa liberada sem brinde no carrinho.
   * @param {{item?: object}} [detail] - detalhe do mb:cart:updated (item adicionado, quando houver)
   */
  async sync(detail) {
    const remove = (this.dataset.giftsRemove || '').split(' ').filter(Boolean);
    const fix = (this.dataset.giftsFix || '').split(' ').filter(Boolean);
    if (remove.length || fix.length) {
      const updates = {};
      remove.forEach((key) => { updates[key] = 0; });
      fix.forEach((key) => { updates[key] = 1; });
      try {
        const response = await fetch(`${window.theme.routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (response.ok) emit('mb:cart:updated', {});
      } catch (error) {
        /* na próxima alteração do carrinho tenta de novo */
      }
      return;
    }
    const added = detail?.item;
    if (!added) return;
    const lines = Array.isArray(added.items) ? added.items : [added];
    if (lines.some((line) => line?.properties?._brinde)) return;
    if (Number(this.dataset.unlocked) > 0 && this.dataset.hasGift !== 'true') this.openPopup();
  }

  openPopup() {
    this.popup?.classList.add('-active');
    document.documentElement.classList.add('cart-open');
  }

  closePopup() {
    this.popup?.classList.remove('-active');
    if (!document.querySelector('mb-cart-drawer.-visible')) {
      document.documentElement.classList.remove('cart-open');
    }
  }

  hideWidget() {
    if (this.widget) this.widget.hidden = true;
    storage.set(HIDDEN_KEY, true, 1);
  }

  selectTab(index) {
    this.querySelectorAll('[data-tab-offer]').forEach((tab) => {
      tab.classList.toggle('-active', tab.dataset.tabOffer === index);
    });
    this.querySelectorAll('[data-offers-index]').forEach((panel) => {
      panel.classList.toggle('-active', panel.dataset.offersIndex === index);
    });
  }

  toggleForm(card, open) {
    if (!card) return;
    this.querySelectorAll('[data-gift-form]').forEach((form) => {
      form.hidden = true;
      form.classList.remove('-active');
    });
    const form = card.querySelector('[data-gift-form]');
    if (!form || !open) return;
    form.hidden = false;
    requestAnimationFrame(() => form.classList.add('-active'));
  }

  async add(button) {
    const card = button.closest('[data-gift-product]');
    const error = card?.querySelector('[data-gift-error]');
    if (error) error.textContent = '';
    button.classList.add('-adding');

    try {
      const response = await fetch(`${window.theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(button.dataset.giftVariant),
          quantity: 1,
          properties: { _brinde: button.dataset.giftTier || '' },
        }),
      });
      const data = await response.json();
      if (!response.ok) throw data;
      this.closePopup();
      emit('mb:cart:updated', { item: data, open: true });
    } catch (data) {
      const strings = (window.theme && window.theme.strings) || {};
      if (error) error.textContent = data.description || data.message || strings.addError || '';
    }
    button.classList.remove('-adding');
  }

  /** Re-renderiza a seção (as faixas desbloqueadas mudam com o total do carrinho). */
  async refresh() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', 'cart-gifts');
      const response = await fetch(url.toString());
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('mb-cart-gifts');
      const wasOpen = this.popup?.classList.contains('-active');
      this.innerHTML = next ? next.innerHTML : '';
      ['unlocked', 'hasGift', 'giftsRemove', 'giftsFix'].forEach((key) => {
        this.dataset[key] = next?.dataset[key] ?? '';
      });
      this.widget = this.querySelector('.popup-offers-widget');
      this.popup = this.querySelector('[data-popup-offers]');
      if (storage.get(HIDDEN_KEY) && this.widget) this.widget.hidden = true;
      if (wasOpen) this.openPopup();
    } catch (error) {
      /* mantém o conteúdo atual */
    }
  }
}

if (!customElements.get('mb-cart-gifts')) customElements.define('mb-cart-gifts', MbCartGifts);
